// jve-5-1: locale-aware formatting helpers. Per the project rule (no hard-coded locales): callers
// pass the resolved locale (from Refine's useGetLocale → i18nProvider), and we use named Intl styles
// rather than ad-hoc patterns. Returns "—" for an absent/invalid value so screens stay clean.
export function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Numeric, like formatDate: a screen that writes "03-07-2026" in one column and "3 jul 2026, 12:35"
  // in the next is its own kind of wrong.
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** Euro currency, locale-aware (no hard-coded locale): the caller passes the resolved locale. */
export function formatEuro(amount: number | null | undefined, locale: string): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(amount);
}

/** A Date's LOCAL calendar day as ISO yyyy-MM-dd. NOT `toISOString().slice(0, 10)` — that is the
 * UTC date, which between 00:00 and 01:00/02:00 CET/CEST still yields YESTERDAY's date. */
export function localDateIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Today as an ISO yyyy-MM-dd string, in local time (see localDateIso). */
export function todayIso(): string {
  return localDateIso(new Date());
}

/**
 * Date-only variant (e.g. a `LocalDate` like "2020-05-01"). Numeric with padded parts — "03-08-2026",
 * the notation the Jmix screens use throughout; a named month ("3 aug 2026") wraps a table column
 * onto two lines and reads differently from the app it replaces.
 */
export function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  // A bare yyyy-MM-dd parses as UTC midnight; format in UTC so the calendar day never shifts.
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

/**
 * Dezelfde dag, maar met zijn weekdag ervoor: "za 19 sep 2026".
 *
 * <p>Voor een aankomst of een vertrek. Wie een pension inplant denkt in weekdagen -- of iemand op een
 * zaterdag komt halen scheelt bezetting -- en dat uit "19-09-2026" afleiden is hoofdrekenen dat het
 * scherm net zo goed kan doen. Alleen daar gebruiken: in een tabelkolom wordt het te lang.
 */
export function formatDateMetDag(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  const d = new Date(/^d{4}-d{2}-d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}
